import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  BaseEdge,
  getBezierPath,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  useStoreApi,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './PipelineEditor.css';
import { FlowZoomBridge, useFlowZoom } from '../utils/flowZoom';
import registry from '../data/entityRegistry.gen.json';
import { agentOsProblems, sideEffectKind, portsOf } from '../data/agentosGraph';
import { useEntityDoc, updateEntity } from '../data/entities';
import { queueFanoutJob, useJob } from '../data/firestore';
import { getIdToken } from '../data/authPlane';
import { kitEnv } from '../data/kitEnv.js';

const API = kitEnv('UPLOAD_API', process.env.REACT_APP_UPLOAD_API || '');

// Visual pipeline editor: React Flow canvas over the pipeline's graph
// ({ blocks, connections } — the exact shape backend/runJob.js executes).
// (Ported from the nventr-io platform; kit adaptation: flat /pipelines/:id
// routes, batch runs only — no streaming mode or per-block compute tiers.)
//
// The palette and config forms render from the generated block catalog, so a
// new block type in backend/shared/blocks/catalog.js shows up here after
// `node scripts/generate-entities.mjs` with zero editor changes.
//
// During a run the canvas is live: the runner publishes per-block state to
// jobs/{id}.liveBlocks (queued → running → done/skipped/error) and the
// custom node/edge components below render it as processing lights and
// data packets streaming along the wires.
const BLOCK_TYPES = registry.blockTypes || {};
const EXECUTION_PROFILES = registry.executionProfiles || {};

const CATEGORY_COLOR = { input: '#3b82f6', transform: '#a78bfa', control: '#f59e0b', output: '#34d399' };

const STATUS_LABEL = { queued: 'queued', running: 'processing', done: 'done', skipped: 'skipped', error: 'failed' };

function NQubeNode({ data, selected }) {
  const def = BLOCK_TYPES[data.blockType] || {};
  const color = CATEGORY_COLOR[def.category] || '#64748b';
  const run = data.run;
  const status = run?.status;
  // Gate blocks render one labelled source handle per port (static catalog
  // ports, or derived from the config for switch/decision); the handle id
  // becomes the connection's fromPort.
  const ports = portsOf({ type: data.blockType, config: data.config }, def);
  const cls = [
    'nq-node',
    status ? `nq-${status}` : '',
    run?.gateClosed ? 'nq-gate-closed' : '',
    selected ? 'nq-selected' : '',
  ].filter(Boolean).join(' ');
  return (
    <div className={cls}>
      <Handle type="target" position={Position.Left} className="nq-handle" />
      <div className="nq-node-head">
        <span className="nq-cat-dot" style={{ background: color }} />
        <span className="nq-node-title">{data.label}</span>
        {status && <span className={`nq-light nq-light-${run?.gateClosed ? 'skipped' : status}`} />}
      </div>
      <div className="nq-node-type">{def.label || data.blockType}</div>
      {status && (
        <div className="nq-node-status">
          {run.gateClosed ? '⨂ branch closed' : run.port ? `→ ${run.port}` : STATUS_LABEL[status] || status}
          {status === 'done' && !run.gateClosed && typeof run.ms === 'number' ? ` · ${(run.ms / 1000).toFixed(1)}s` : ''}
        </div>
      )}
      {run?.preview && !run.gateClosed && <div className="nq-node-preview">{run.preview}</div>}
      {run?.error && <div className="nq-node-error">{run.error}</div>}
      {ports ? (
        ports.map((p, i) => (
          <Handle
            key={p}
            id={p}
            type="source"
            position={Position.Right}
            className={`nq-handle nq-handle-port nq-handle-${i === 0 ? 'primary' : 'secondary'}${run?.port === p || (i === 0 && run && !run.port && !run.gateClosed && status === 'done') ? ' is-live' : ''}`}
            style={{ top: `${ports.length === 1 ? 50 : 22 + (i * 56) / (ports.length - 1)}%` }}
            title={`${p} port`}
          >
            <span className="nq-port-label">{p}</span>
          </Handle>
        ))
      ) : (
        <Handle type="source" position={Position.Right} className="nq-handle" />
      )}
    </div>
  );
}

// Edge that visualizes data movement: animated packets while upstream is
// producing (or output is queued for the next block), solid green once the
// hand-off completed, dashed + dim for branches that died at a gate.
function PacketEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data }) {
  const [path] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
  const state = data?.state || 'idle';
  const style = {
    idle: { stroke: 'rgba(148,163,184,0.45)', strokeWidth: 1.6 },
    flowing: { stroke: '#22d3ee', strokeWidth: 2.4 },
    done: { stroke: 'rgba(52,211,153,0.85)', strokeWidth: 2 },
    dead: { stroke: 'rgba(148,163,184,0.22)', strokeWidth: 1.4, strokeDasharray: '5 5' },
  }[state];
  return (
    <>
      <BaseEdge id={id} path={path} style={style} />
      {state === 'flowing' && [0, 0.45, 0.9].map((begin) => (
        <circle key={begin} r="3.5" className="nq-packet">
          <animateMotion dur="1.35s" begin={`${begin}s`} repeatCount="indefinite" path={path} />
        </circle>
      ))}
    </>
  );
}

// `port` = the edge's fromPort (undefined = primary). An edge is dead when the
// upstream output left on a different port than the edge hangs off.
const PRIMARY_PORTS = new Set(['pass', 'approve']);
function edgeState(src, tgt, jobActive, port) {
  if (!src && !tgt) return 'idle';
  if (src?.gateClosed || src?.status === 'skipped' || tgt?.status === 'skipped') return 'dead';
  if (src?.status === 'done') {
    const left = src.port || 'primary';
    const want = !port || PRIMARY_PORTS.has(port) ? 'primary' : port;
    if (left !== want) return 'dead';
  }
  if (src?.status === 'running') return 'flowing';
  if (src?.status === 'done' && (tgt?.status === 'running' || (jobActive && tgt?.status === 'queued'))) return 'flowing';
  if (src?.status === 'done') return 'done';
  return 'idle';
}

const NODE_TYPES = { nqube: NQubeNode };
const EDGE_TYPES = { packet: PacketEdge };

const toNodes = (graph) =>
  (graph?.blocks || []).map((b) => ({
    id: b.id,
    type: 'nqube',
    position: b.position || { x: 80, y: 80 },
    data: {
      label: `${b.label || BLOCK_TYPES[b.type]?.label || b.type}`,
      blockType: b.type,
      config: b.config || {},
    },
  }));

// fromPort ↔ React Flow sourceHandle. Gate nodes render one handle per port
// (id = port name); plain nodes have an anonymous handle (no fromPort).
const toEdges = (graph) =>
  (graph?.connections || []).map((c) => ({
    id: c.id || `${c.from}-${c.to}${c.fromPort ? `-${c.fromPort}` : ''}`,
    source: c.from,
    target: c.to,
    ...(c.fromPort ? { sourceHandle: c.fromPort, label: c.fromPort } : {}),
  }));

const toGraph = (nodes, edges) => ({
  blocks: nodes.map((n) => ({
    id: n.id,
    type: n.data.blockType,
    label: n.data.label,
    position: { x: Math.round(n.position.x), y: Math.round(n.position.y) },
    config: n.data.config || {},
  })),
  connections: edges.map((e) => ({
    id: e.id,
    from: e.source,
    to: e.target,
    ...(e.sourceHandle ? { fromPort: e.sourceHandle } : {}),
  })),
});

export default function PipelineEditor() {
  const { pipelineId } = useParams();
  const scope = useMemo(() => ({}), []);
  const { doc: pipeline } = useEntityDoc('pipeline', scope, pipelineId);

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const canvasRef = useRef(null);
  const nodeKey = nodes.map((n) => `${n.id}:${Math.round((n.position?.x || 0) / 80)}:${Math.round((n.position?.y || 0) / 80)}`).join('|');
  const { minZoom, maxZoom, onInit, placeRef } = useFlowZoom(canvasRef, {
    blockWidth: 180,
    blockHeight: 78,
    nodeKey,
  });
  const [caps, setCaps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [runError, setRunError] = useState('');
  // lastRunJobId is server-stamped by the runner's prep task — track the id
  // we just queued locally so run status is live before the runner starts.
  const [queuedJobId, setQueuedJobId] = useState(null);
  const { job, tasks } = useJob(queuedJobId || pipeline?.lastRunJobId || null);

  // Hydrate the canvas once from Firestore (afterwards local state is truth
  // until save — a live re-sync would fight the user's drag in progress).
  useEffect(() => {
    if (pipeline && !loaded) {
      const nextCaps = Array.isArray(pipeline.capabilities) ? pipeline.capabilities : [];
      setNodes(toNodes(pipeline.graph));
      setEdges(toEdges(pipeline.graph));
      setCaps(nextCaps);
      const problems = agentOsProblems(pipeline.graph, nextCaps);
      if (problems.length) setRunError(problems.join(' '));
      setLoaded(true);
    }
  }, [pipeline, loaded]);

  const onNodesChange = useCallback((changes) => {
    setNodes((ns) => applyNodeChanges(changes, ns));
    if (changes.some((c) => c.type !== 'select')) setDirty(true);
  }, []);
  const onEdgesChange = useCallback((changes) => {
    setEdges((es) => applyEdgeChanges(changes, es));
    if (changes.some((c) => c.type !== 'select')) setDirty(true);
  }, []);
  const onConnect = useCallback((conn) => {
    setEdges((es) => addEdge(conn, es));
    setDirty(true);
  }, []);

  const addBlock = (type) => {
    const id = `b${Date.now().toString(36)}`;
    const def = BLOCK_TYPES[type];
    setNodes((ns) => [
      ...ns,
      ...toNodes({ blocks: [{ id, type, label: def?.label || type, position: { x: 120 + ns.length * 30, y: 100 + ns.length * 30 } }] }),
    ]);
    setDirty(true);
  };

  const graphError = () => {
    const problems = agentOsProblems(toGraph(nodes, edges), caps);
    return problems.length ? problems.join(' ') : '';
  };

  // Drafts always persist — a graph that still fails the gate rules (e.g. a
  // webhook with no capability yet) is saved as-is with the problem shown
  // inline; the rules block RUNNING, and the backend re-asserts them before
  // any step executes.
  const save = async () => {
    setSaving(true);
    try {
      await updateEntity('pipeline', scope, pipelineId, { graph: toGraph(nodes, edges), capabilities: caps });
      setDirty(false);
      setRunError(graphError());
    } finally {
      setSaving(false);
    }
  };

  const run = async () => {
    setRunError('');
    try {
      const err = graphError();
      if (err) throw new Error(err);
      if (dirty) await save();
      const jobId = await queueFanoutJob(
        'pipeline',
        { pipelineId },
        { title: `pipeline: ${pipeline?.name || pipelineId}` }
      );
      setQueuedJobId(jobId);
      // lastRunJobId is server-managed: runJob's prep stamps it (admin SDK).
    } catch (e) {
      setRunError(e.message);
    }
  };

  const setProfile = async (profile) => {
    setRunError('');
    try {
      await updateEntity('pipeline', scope, pipelineId, {
        execution: { ...(pipeline.execution || {}), profile },
      });
    } catch (e) { setRunError(e.message); }
  };

  const jobActive = !!job && ['queued', 'planning', 'running', 'stitching'].includes(job.status);
  // Dry run (POST /pipelines/:id/dry-run): the unsaved draft runs inline
  // under a budget with no side effects; its per-block ports/outputs light
  // the canvas like a real run would.
  const [dry, setDry] = useState(null); // { busy, result, error }
  const dryRun = async (callInput) => {
    setDry({ busy: true });
    try {
      const token = await getIdToken();
      let input = callInput;
      try { input = JSON.parse(callInput); } catch { /* plain text */ }
      const res = await fetch(`${API}/pipelines/${pipelineId}/dry-run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ callInput: input, graph: toGraph(nodes, edges) }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `dry run failed (${res.status})`);
      setDry({ result: json });
    } catch (e) { setDry({ error: e.message }); }
  };
  const dryBlocks = useMemo(() => {
    const r = dry?.result;
    if (!r) return null;
    const out = {};
    for (const id of Object.keys(r.ports || {})) {
      const port = r.ports[id];
      const v = r.outputs?.[id];
      const preview = v == null ? '' : (typeof v.text === 'string' ? v.text : JSON.stringify(v)).replace(/\s+/g, ' ').trim().slice(0, 90);
      out[id] = port === null ? { status: 'done', gateClosed: true, preview: 'branch closed' } : { status: 'done', preview, ...(port !== 'primary' ? { port } : {}) };
    }
    for (const n of nodes) if (!out[n.id]) out[n.id] = { status: 'skipped' };
    return out;
  }, [dry, nodes]);
  const liveBlocks = job?.liveBlocks || dryBlocks || null;

  // Overlay live telemetry onto the editable graph. Base nodes/edges stay the
  // editing source of truth; these derived copies just add run state for the
  // custom renderers.
  const rfNodes = useMemo(
    () =>
      nodes.map((n) => (liveBlocks?.[n.id] ? { ...n, data: { ...n.data, run: liveBlocks[n.id] } } : n)),
    [nodes, liveBlocks]
  );
  const rfEdges = useMemo(
    () =>
      edges.map((e) => ({
        ...e,
        type: 'packet',
        data: { state: liveBlocks ? edgeState(liveBlocks[e.source], liveBlocks[e.target], jobActive, e.sourceHandle) : 'idle' },
      })),
    [edges, liveBlocks, jobActive]
  );

  if (pipeline === undefined) return <div className="cs-page"><p className="cs-help">Loading…</p></div>;
  if (pipeline === null) {
    return (
      <div className="cs-page">
        <p className="cs-help">Pipeline not found. <Link to="/pipelines">Back</Link></p>
      </div>
    );
  }

  const selectedNode = nodes.find((n) => n.id === selected);

  return (
    <div className="cs-page" style={{ maxWidth: 'none' }}>
      <p style={{ margin: 0 }}>
        <Link to="/pipelines" className="cs-help" style={{ textDecoration: 'none' }}>
          ← Pipelines
        </Link>
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
        <h1 style={{ margin: 0 }}>{pipeline.name}</h1>
        {jobActive && <span className="nq-run-badge"><span className="nq-light nq-light-running" /> running</span>}
        <span style={{ flex: 1 }} />
        <select
          className="cs-input"
          style={{ width: 150, padding: '4px 8px', fontSize: 13 }}
          title={EXECUTION_PROFILES[pipeline?.execution?.profile || 'economy']?.help || 'Execution profile'}
          value={pipeline?.execution?.profile || 'economy'}
          onChange={(e) => setProfile(e.target.value)}
        >
          {Object.entries(EXECUTION_PROFILES).map(([k, p]) => (
            <option key={k} value={k}>{p.label || k}</option>
          ))}
        </select>
        <button className="cs-btn cs-btn-outline cs-btn-sm" disabled={!dirty || saving} onClick={save}>
          {saving ? 'Saving…' : dirty ? 'Save' : 'Saved ✓'}
        </button>
        <button
          className="cs-btn cs-btn-primary cs-btn-sm"
          disabled={!nodes.length || jobActive}
          onClick={run}
          data-agent-noclick
        >
          Run
        </button>
      </div>
      {runError && <p className="cs-help" style={{ color: 'var(--danger, #e5484d)' }}>{runError}</p>}

      <div style={{ display: 'flex', gap: 12, marginTop: 14, alignItems: 'stretch' }}>
        {/* Palette */}
        <div style={{ width: 170, flexShrink: 0 }}>
          <span className="cs-help" style={{ fontSize: 12 }}>Add block</span>
          <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
            {Object.entries(BLOCK_TYPES).map(([type, def]) => (
              <button
                key={type}
                className="cs-btn cs-btn-outline cs-btn-sm"
                style={{ borderColor: CATEGORY_COLOR[def.category], textAlign: 'left' }}
                title={def.description}
                onClick={() => addBlock(type)}
              >
                {def.label}
              </button>
            ))}
          </div>
        </div>

        {/* Canvas */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div ref={canvasRef} className="cs-card" style={{ height: 520, padding: 0, overflow: 'hidden' }}>
            <ReactFlow
              nodes={rfNodes}
              edges={rfEdges}
              nodeTypes={NODE_TYPES}
              edgeTypes={EDGE_TYPES}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={(_e, n) => setSelected(n.id)}
              onPaneClick={() => setSelected(null)}
              onInit={onInit}
              minZoom={minZoom}
              maxZoom={maxZoom}
              colorMode="dark"
            >
              <FlowZoomBridge useStoreApi={useStoreApi} placeRef={placeRef} />
              <Background />
              <Controls />
              <MiniMap
                pannable
                zoomable
                nodeColor={(n) => CATEGORY_COLOR[BLOCK_TYPES[n.data?.blockType]?.category] || '#475569'}
                maskColor="rgba(2,6,12,0.55)"
                style={{ background: 'rgba(10,14,20,0.9)' }}
              />
            </ReactFlow>
          </div>
          <div className="nq-legend">
            <span><i className="nq-light nq-light-queued" /> queued</span>
            <span><i className="nq-light nq-light-running" /> processing</span>
            <span><i className="nq-light nq-light-done" /> done</span>
            <span><i className="nq-light nq-light-skipped" /> skipped branch</span>
            <span><svg width="26" height="8"><line x1="0" y1="4" x2="26" y2="4" stroke="#22d3ee" strokeWidth="2" /><circle cx="13" cy="4" r="3" fill="#22d3ee" /></svg> data in flight</span>
          </div>
        </div>

        {/* Config panel */}
        <div style={{ width: 260, flexShrink: 0 }}>
          <CapabilitiesPanel
            caps={caps}
            onChange={(next) => { setCaps(next); setDirty(true); }}
          />
          {selectedNode ? (
            <BlockConfig
              node={selectedNode}
              caps={caps}
              onChange={(patch) => {
                setNodes((ns) => ns.map((n) => (n.id === selectedNode.id ? { ...n, data: { ...n.data, ...patch } } : n)));
                setDirty(true);
              }}
              onDelete={() => {
                setNodes((ns) => ns.filter((n) => n.id !== selectedNode.id));
                setEdges((es) => es.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
                setSelected(null);
                setDirty(true);
              }}
            />
          ) : (
            <>
              <DryRunPanel dry={dry} onRun={dryRun} hasCallInput={nodes.some((n) => n.data.blockType === 'input.call')} />
              <RunStatus job={job} tasks={tasks} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const CAP_KINDS = [
  ['s3', 'S3 prefix'],
  ['dataset', 'Dataset'],
  ['knowledge', 'Knowledge audience'],
  ['webhook', 'Webhook URL'],
];

function CapabilitiesPanel({ caps, onChange }) {
  const patch = (i, field, value) => {
    onChange(caps.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)));
  };
  return (
    <div className="cs-card" style={{ padding: 12, marginBottom: 10 }}>
      <strong style={{ color: 'var(--text)', fontSize: 13 }}>Capabilities</strong>
      <p className="cs-help" style={{ fontSize: 12, marginTop: 6 }}>
        Systems this pipeline may write to. A side effect has to name one. Webhook secrets stay in SSM under the name you type.
      </p>
      {caps.map((c, i) => (
        <div key={i} style={{ display: 'grid', gap: 4, marginTop: 8 }}>
          <input className="cs-input" placeholder="id" value={c.id || ''} onChange={(e) => patch(i, 'id', e.target.value)} />
          <select className="cs-input" value={c.kind || 'webhook'} onChange={(e) => patch(i, 'kind', e.target.value)}>
            {CAP_KINDS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
          <input
            className="cs-input"
            placeholder={c.kind === 'webhook' ? 'https://…' : 'target'}
            value={c.target || ''}
            onChange={(e) => patch(i, 'target', e.target.value)}
          />
          {c.kind === 'webhook' && (
            <input
              className="cs-input"
              placeholder="secret name"
              value={c.secretKey || ''}
              onChange={(e) => patch(i, 'secretKey', e.target.value)}
            />
          )}
          <button className="cs-btn cs-btn-outline cs-btn-sm" type="button" onClick={() => onChange(caps.filter((_, idx) => idx !== i))}>
            Remove
          </button>
        </div>
      ))}
      <button
        className="cs-btn cs-btn-outline cs-btn-sm"
        type="button"
        style={{ marginTop: 8 }}
        onClick={() => onChange([...caps, { id: `cap${caps.length + 1}`, kind: 'webhook', target: '', secretKey: '' }])}
      >
        Add capability
      </button>
    </div>
  );
}

function BlockConfig({ node, caps, onChange, onDelete }) {
  const def = BLOCK_TYPES[node.data.blockType] || { config: [] };
  const config = node.data.config || {};
  return (
    <div className="cs-card" style={{ padding: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <strong style={{ color: 'var(--text)', fontSize: 13 }}>{def.label || node.data.blockType}</strong>
        <span style={{ flex: 1 }} />
        <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={onDelete}>✕</button>
      </div>
      {def.description && <p className="cs-help" style={{ fontSize: 12, marginTop: 6 }}>{def.description}</p>}

      <label className="cs-help" style={{ display: 'block', marginTop: 10, fontSize: 12 }}>Label</label>
      <input
        className="cs-input"
        style={{ width: '100%', marginTop: 4 }}
        value={node.data.label}
        onChange={(e) => onChange({ label: e.target.value })}
      />

      {(def.config || []).map((f) => (
        <div key={f.key}>
          <label className="cs-help" style={{ display: 'block', marginTop: 10, fontSize: 12 }}>
            {f.label}
            {f.help ? ` — ${f.help}` : ''}
          </label>
          {f.key === 'capabilityId' ? (
            <select
              className="cs-input"
              style={{ width: '100%', marginTop: 4 }}
              value={config.capabilityId || ''}
              onChange={(e) => onChange({ config: { ...config, capabilityId: e.target.value } })}
            >
              <option value="">Select a capability</option>
              {(caps || []).filter((c) => c.kind === sideEffectKind(node.data.blockType)).map((c) => (
                <option key={c.id} value={c.id}>{c.id} · {c.target || c.kind}</option>
              ))}
            </select>
          ) : f.type === 'textarea' ? (
            <textarea
              className="cs-input"
              style={{ width: '100%', minHeight: 80, marginTop: 4 }}
              value={config[f.key] || ''}
              onChange={(e) => onChange({ config: { ...config, [f.key]: e.target.value } })}
            />
          ) : f.type === 'select' ? (
            <select
              className="cs-input"
              style={{ width: '100%', marginTop: 4 }}
              value={config[f.key] || f.options?.[0] || ''}
              onChange={(e) => onChange({ config: { ...config, [f.key]: e.target.value } })}
            >
              {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input
              className="cs-input"
              style={{ width: '100%', marginTop: 4 }}
              value={config[f.key] || ''}
              onChange={(e) => onChange({ config: { ...config, [f.key]: e.target.value } })}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// "Test with a sample request": runs the draft inline (no side effects,
// budgeted) and shows which port each gate took plus the Return value.
function DryRunPanel({ dry, onRun, hasCallInput }) {
  const [input, setInput] = useState('{"text": "fix the typo in the Save button label"}');
  const r = dry?.result;
  return (
    <div className="cs-card" style={{ padding: 12, marginBottom: 10 }}>
      <strong style={{ color: 'var(--text)', fontSize: 13 }}>Test with a sample request</strong>
      <p className="cs-help" style={{ fontSize: 12, marginTop: 6 }}>
        Runs the draft here, no side effects. {hasCallInput ? 'The text below is the Call input (JSON or plain text).' : 'Add a “Call input” block to feed the request in.'}
      </p>
      <textarea className="cs-input" style={{ width: '100%', minHeight: 64, marginTop: 4, fontFamily: 'ui-monospace, monospace', fontSize: 12 }} value={input} onChange={(e) => setInput(e.target.value)} aria-label="sample request" />
      <button className="cs-btn cs-btn-outline cs-btn-sm" style={{ marginTop: 6 }} disabled={dry?.busy} onClick={() => onRun(input)}>{dry?.busy ? 'Running…' : 'Dry run'}</button>
      {dry?.error && <p className="cs-help" style={{ color: 'var(--danger, #e5484d)', fontSize: 12, marginTop: 6 }} role="alert">{dry.error}</p>}
      {r && (
        <div style={{ marginTop: 8 }}>
          <p className="cs-help" style={{ fontSize: 12, margin: 0 }}>{r.blocks} ran · {r.skipped} skipped · {r.modelCalls} model call{r.modelCalls === 1 ? '' : 's'} · {r.ms} ms</p>
          <pre className="cs-help" style={{ fontSize: 11.5, whiteSpace: 'pre-wrap', margin: '6px 0 0', maxHeight: 180, overflow: 'auto' }}>{JSON.stringify(r.value && Object.keys(r.value).length ? r.value : r.text, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

function RunStatus({ job, tasks }) {
  if (!job) {
    return (
      <div className="cs-card" style={{ padding: 12 }}>
        <p className="cs-help" style={{ margin: 0, fontSize: 12 }}>
          Select a block to configure it. Run status appears here.
        </p>
      </div>
    );
  }
  const results = job.output?.results || [];
  const live = job.liveBlocks || {};
  const counts = Object.values(live).reduce((acc, b) => {
    const k = b.gateClosed ? 'done' : b.status;
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  return (
    <div className="cs-card" style={{ padding: 12 }}>
      <strong style={{ color: 'var(--text)', fontSize: 13 }}>Last run — {job.status}</strong>
      {Object.keys(live).length > 0 && (
        <p className="cs-help" style={{ fontSize: 12, margin: '6px 0 0' }}>
          {['running', 'queued', 'done', 'skipped', 'error']
            .filter((k) => counts[k])
            .map((k) => `${counts[k]} ${k}`)
            .join(' · ')}
        </p>
      )}
      {(tasks || []).map((t) => (
        <p key={t.id} className="cs-help" style={{ fontSize: 12, margin: '6px 0 0' }}>
          {t.role}: {t.stage || t.status}
        </p>
      ))}
      {results.map((r) => (
        <p key={r.blockId} className="cs-help" style={{ fontSize: 12, margin: '6px 0 0' }}>
          {r.skipped ? '⤳' : r.ok ? '✓' : '✗'} {r.label || r.type}
          {r.skipped ? ' — skipped (branch not taken)' : ''}
          {r.error ? ` — ${r.error}` : ''}
        </p>
      ))}
      {job.output?.outputs && (
        <details style={{ marginTop: 8 }}>
          <summary className="cs-help" style={{ fontSize: 12, cursor: 'pointer' }}>Block outputs</summary>
          <pre style={{ fontSize: 11, overflow: 'auto', maxHeight: 200 }}>
            {JSON.stringify(job.output.outputs, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}
